import useSWR from 'swr'
import React from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'

import { MasterPage, OnboardingMaster, Home, OnboardingPersonal, Content, Video, Document, EventsPage, EventPage, AdvisorAI, Companies, Company, UserProfile, CompanySubDivision, AccessExpired, SearchPage, SearchMobilePage, OnboardingStudies, OnboardingPreferences, OnboardingNews, AiInterview, JobOffersPage, CoursesPage, CoursePage, ConnectLinkedin, LinkedinVerification, CVRoaster, JobOfferApplication, CheckoutPage, CheckoutSuccessPage, BillingPortalPage, AlreadyBoughtPage, JobOfferCVRoaster, JobOfferAiInterview, UpgradePro, UserBookmarks, UserHistory, ZriveReferralProgram } from './ui/pages'
import BackgroundWrapper from './ui/components/layout/BackgroundWrapper'
import GlobalErrorHandler from './libraries/errors/GlobalHandler'
import PostHogPageviewTracker from './libraries/posthog'
import { getUserPreferences, getUserStudies } from './api/users'
import Loading from './ui/components/layout/Loading'
import NotFoundPage from './ui/pages/404'
import DocumentReader from './ui/pages/DocumentReader'
import { ToasterProvider } from './libraries/toaster'
import { FeedbackButton } from './ui/components/feedback/FeedbackButton'
import Login from './ui/pages/Login'
import { useAuth, CurrentUserProvider, CurrentUserContext , CustomAuthProvider } from './libraries/auth/index'
import ResetPassword from './ui/pages/ResetPassword'
import ConfirmResetPassword from './ui/pages/ConfirmResetPassword'
import VerifyEmail from './ui/pages/VerifyEmail'
import { showLinkedinVerificationBeforeOnBoarding } from './libraries/auth/linkedinVerification'
import FilterProviders from './libraries/search/filters'
import InternalOffersFaqs from './ui/pages/InternalOffersFaqs'

function App() {
	return (
		<GlobalErrorHandler>
			<BackgroundWrapper>
				<ToasterProvider>
					<CustomAuthProvider>
						<FilterProviders>
							<AuthRoutes />
						</FilterProviders>
					</CustomAuthProvider>
				</ToasterProvider>
			</BackgroundWrapper>
		</GlobalErrorHandler>
	)
}

// New component to handle auth-based routing
const AuthRoutes = () => {
	const { isAuthenticated, isLoading } = useAuth()

	if (isLoading) {
		return <Loading />
	}

	if(!isAuthenticated) {
		const currentPath = window.location.pathname
		if(currentPath !== '/linkedin-verification') {
			localStorage.setItem('auth_redirect_path', currentPath)
		}
	}

	return (
		<Routes>
			<Route path='/checkout/already-bought' element={<AlreadyBoughtPage />} />
			<Route path='/checkout/success' element={<CheckoutSuccessPage />} />
			<Route path='/checkout/:productSlug/:email?' element={<CheckoutPage />} />
			{/* Auth Routes */}
			{!isAuthenticated ? (
				<>
					<Route path='/reset-password' element={<ResetPassword />} />
					<Route path='/confirm-reset-password' element={<ConfirmResetPassword />} />
					<Route path='/verify-email' element={<VerifyEmail />} />
					<Route path='*' element={<Login />} />
				</>
			) : (
				<>
					<Route path='/login' element={<Navigate to='/' />} />
					<Route path='/reset-password' element={<Navigate to='/' />} />
					<Route path='/confirm-reset-password' element={<Navigate to='/' />} />
					<Route path='/verify-email' element={<Navigate to='/' />} />
					<Route path='/*' element={<AuthenticatedRoutes />} />
				</>
			)}
		</Routes>
	)
}

// Wrapper for authenticated routes that need CurrentUserContext
const AuthenticatedRoutes = () => {
	return (
		<CurrentUserProvider>
			<PostHogPageviewTracker />
			<ProtectedRoutes />
			<FeedbackButton />
		</CurrentUserProvider>
	)
}

// New component to wrap authenticated routes
const ProtectedRoutes = () => {
	const { currentUser } = React.useContext(CurrentUserContext)
	return (
			<Routes>
				<Route element={<MasterPage fullHeight hideNavBar />}>
					<Route element={<OnboardingMaster width='md' />}>
						<Route path='/onboarding' element={showLinkedinVerificationBeforeOnBoarding(!!currentUser?.onboardingCompleted) ? <Navigate to='/onboarding/linkedin' /> : <Navigate to='/onboarding/personal' /> } />
						<Route element={<OnboardingPagesRedirections />}>
							<Route path='/onboarding/personal' element={<OnboardingPersonal />} />
							<Route path='/onboarding/studies' element={<OnboardingStudies />} />
						</Route>
					</Route>
					<Route element={<OnboardingMaster width='lg' />}>
						<Route path='/onboarding/linkedin' element={<ConnectLinkedin />} />
						<Route path='/linkedin-verification' element={<LinkedinVerification />} />
						<Route element={<OnboardingPagesRedirections />}>
							<Route path='/onboarding/preferences' element={<OnboardingPreferences />} />
							<Route path='/onboarding/news' element={<OnboardingNews />} />
						</Route>
					</Route>
				</Route>

				<Route path='/access-expired' element={<AccessExpired />} />

				<Route element={<ProtectedPagesRedirections />}>
					<Route element={<MasterPage noPadding hideFooter hideNavBar />}>
						<Route path='/advisor-ai' element={<AdvisorAI />} />
						<Route path='/cv-matching/job/:id' element={<JobOfferCVRoaster />} />
						<Route path='/cv-roaster' element={<CVRoaster />} />
						<Route path='/ai-interview/:topic/job/:id' element={<JobOfferAiInterview />} />
						<Route path='/ai-interview/:topic?' element={<AiInterview />} />
					</Route>
					<Route element={<MasterPage />}>
						<Route path='/content/videos/:id' element={<Video />} />
						<Route path='/content/documents/:id' element={<Document />} />
						<Route path='/content/documents/:id/reader' element={<DocumentReader />} />
						<Route path='/content' element={<Content />} />
						{/* <Route path='/notifications' element={<Notifications />} /> */}
						<Route path='/events' element={<EventsPage />} />
						<Route path='/events/:id' element={<EventPage />} />
						<Route path='/companies' element={<Companies />} />
						<Route path='/companies/:id' element={<Company />} />
						<Route path='/companies/:companyId/divisions/:divId/sub-divisions/:subDivId' element={<CompanySubDivision />} />
						<Route path='/profile/billing' element={<BillingPortalPage />} />
						<Route path='/profile' element={<UserProfile />} />
						<Route path='/bookmarks' element={<UserBookmarks />} />
						<Route path='/history' element={<UserHistory />} />
						<Route path='/search/:content?' element={<SearchPage />} />
						<Route path='/jobs' element={<JobOffersPage />} />
						<Route path='/jobs/:id/application' element={<JobOfferApplication />} />
						<Route path='/courses' element={<CoursesPage />} />
						<Route path='/courses/:courseId/videos/:id' element={<Video />} />
						<Route path='/courses/:id' element={<CoursePage />} />
						<Route path='/internal-offers-faqs' element={<InternalOffersFaqs />} />
						<Route path='/upgrade-pro' element={<UpgradePro />} />
						<Route path='/zrive-referral-program' element={<ZriveReferralProgram />} />
						<Route path='/' element={<Home />} />
						<Route path='*' element={<NotFoundPage />} />
					</Route>
					<Route element={<MasterPage fullHeight hideNavBar />}>
						<Route path='/search-mobile/:content?' element={<SearchMobilePage />} />
					</Route>
				</Route>
			</Routes>
	)
}

const OnboardingPagesRedirections = () => {
	const { currentUser, isLinkedinConnected } = React.useContext(CurrentUserContext)
	const location = useLocation()

	const PERSONAL_PATH = '/onboarding/personal'
	const PREFERENCES_PATH = '/onboarding/preferences'
	const NEWS_PATH = '/onboarding/news'
	const { data: userStudies, isLoading: studiesLoading } = useSWR('/users/studies', currentUser?.name ? getUserStudies : null)
	const { data: userPreferences, isLoading: preferencesLoading } = useSWR('/users/preferences', currentUser?.name ? getUserPreferences : null)

	if(currentUser && currentUser?.onboardingCompleted && !currentUser?.linkedinSub) { return <Navigate to='/onboarding/linkedin' /> }
	if(!currentUser && !isLinkedinConnected && showLinkedinVerificationBeforeOnBoarding(false)) { return <Navigate to='/onboarding/linkedin' /> }
	if(!currentUser && location.pathname !== PERSONAL_PATH) { return <Navigate to='/onboarding/personal' /> }
	if(preferencesLoading || studiesLoading) { return <Loading /> }
	if(!userStudies && (location.pathname === PREFERENCES_PATH || location.pathname === NEWS_PATH)) { return <Navigate to='/onboarding/studies' /> }
	if(!userPreferences && location.pathname === NEWS_PATH) { return <Navigate to='/onboarding/preferences' /> }

	return <Outlet />
}

const ProtectedPagesRedirections = () => {
	const { currentUser } = React.useContext(CurrentUserContext)
	const { logout, isAuthenticated, isLoading } = useAuth()
	if(isAuthenticated && !isLoading && !currentUser) {
		console.log('Authenticated but no current user found, redirecting to onboarding')
		logout().then(() => {
			window.location.reload()
			return <Loading />
		})
	}
	if(!currentUser || currentUser.shouldCompleteOnboarding) {
		return <Navigate to='/onboarding' />
	}
	if(currentUser && !currentUser.access?.active) {
		return <Navigate to='/access-expired' />
	}
	return <Outlet />
}

export default App
